// pages/PaymentReturn.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { queryClient } from '@/lib/queryClient';
import { useCurrentPackage, PAYMENTS_KEYS } from '@/hooks/usePayments';
import { useLocation } from 'wouter';

export default function PaymentReturn() {
  const [, setLocation] = useLocation();
  const { data, isLoading, refetch } = useCurrentPackage();

  const [status, setStatus] = useState<'processing'|'success'|'timeout'|'error'>('processing');
  const initial = useRef<{ credits?: number; lastPurchaseAt?: string | undefined } | null>(null);

  // Capture initial snapshot once
  useEffect(() => {
    if (!initial.current && data) {
      initial.current = { credits: data.credits_balance, lastPurchaseAt: data.last_purchase_at };
    }
  }, [data]);

  // Poll every 2s up to 30s
  useEffect(() => {
    let tries = 0;
    const maxTries = 15; // ~30s
    let timer: number | null = null;

    const tick = async () => {
      try {
        // Ask React Query to refetch current package
        await queryClient.refetchQueries({ queryKey: PAYMENTS_KEYS.current, type: 'active' });
        const res = await refetch();
        const cur = res.data;
        const base = initial.current;
        if (cur && base) {
          const creditsIncreased = typeof cur.credits_balance === 'number' && typeof base.credits === 'number' && cur.credits_balance > base.credits;
          const purchaseChanged = (cur.last_purchase_at || '') !== (base.lastPurchaseAt || '');
          if (creditsIncreased || purchaseChanged) {
            setStatus('success');
            // Refresh related UI queries eagerly
            queryClient.invalidateQueries({ queryKey: ["/api/credits/balance"] });
            queryClient.invalidateQueries({ queryKey: ["/api/user/profile"] });
            return;
          }
        }
      } catch {
        // ignore and continue, but if repeated errors occur we time out naturally
      }
      tries += 1;
      if (tries >= maxTries) {
        setStatus('timeout');
        return;
      }
      timer = window.setTimeout(tick, 2000) as unknown as number;
    };

    // Start once we have an initial snapshot (or immediately if not required)
    timer = window.setTimeout(tick, 2000) as unknown as number;
    return () => { if (timer) window.clearTimeout(timer as unknown as number); };
  }, [refetch]);

  const content = useMemo(() => {
    if (status === 'success') {
      return (
        <div className="text-center">
          <div className="text-emerald-400 text-lg font-semibold mb-2">Payment confirmed</div>
          <div className="text-gray-300 mb-6">Your tokens have been updated.</div>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => setLocation('/dashboard')} className="bg-[#00FF8C] text-black hover:brightness-105">Go to Dashboard</Button>
            <Button variant="ghost" onClick={() => setLocation('/pricing')} className="text-white border border-white/20">Back to Pricing</Button>
          </div>
        </div>
      );
    }
    if (status === 'timeout') {
      return (
        <div className="text-center">
          <div className="text-amber-300 text-lg font-semibold mb-2">Still processing</div>
          <div className="text-gray-300 mb-4">Confirmation is taking longer than usual. You can refresh later or view your history.</div>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => window.location.reload()} className="bg-[#4C73FF] text-white hover:brightness-110">Retry</Button>
            <Button variant="ghost" onClick={() => setLocation('/dashboard')} className="text-white border border-white/20">Go to Dashboard</Button>
          </div>
        </div>
      );
    }
    if (status === 'error') {
      return (
        <div className="text-center">
          <div className="text-red-400 text-lg font-semibold mb-2">Could not verify payment</div>
          <div className="text-gray-300 mb-4">Please try again or contact support if the charge appears on your statement.</div>
          <Button onClick={() => setLocation('/pricing')} className="bg-[#4C73FF] text-white hover:brightness-110">Back to Pricing</Button>
        </div>
      );
    }
    // processing
    return (
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 text-gray-200 mb-3"><LoadingSpinner className="w-4 h-4" />Processing payment…</div>
        <div className="text-gray-400 text-sm">We2re confirming your payment. This usually takes a few seconds.</div>
      </div>
    );
  }, [setLocation, status]);

  return (
    <Layout>
      <SEO
        title="Payment Processing | TradeSagePro"
        description="We are confirming your payment. This will take just a moment."
        canonical="https://tradesagepro.com/payment/return"
        type="website"
      />
      <div className="min-h-screen bg-[#10151D] text-[hsl(210,9%,91%)] flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-[#1C2A44] bg-[hsl(215,15%,14%)] p-8 shadow-[0_0_40px_rgba(12,18,28,0.6)]">
          <h1 className="text-2xl font-bold text-white mb-4 text-center">Payment Status</h1>
          {isLoading && (
            <div className="flex items-center justify-center mb-4"><LoadingSpinner /></div>
          )}
          {content}
        </div>
      </div>
    </Layout>
  );
}
